import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import api from '../config/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // The stored copy is from the last login. Refresh it, so what the server adds later
      // (a workshop's own brand, the catalog switch) arrives without signing out.
      // On any failure the stored copy simply stays.
      api.get('/auth/me').then((res) => {
        const fresh = res.data?.user;
        if (fresh && localStorage.getItem('token') === storedToken) {
          localStorage.setItem('user', JSON.stringify(fresh));
          setUser(fresh);
        }
      }).catch(() => {});
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      // Make login request - ensure no token is sent for login endpoint
      const response = await api.post('/auth/login', { username, password }, {
        headers: {
          Authorization: undefined, // Explicitly remove token for login
        },
      });
      const { token: newToken, user: newUser } = response.data;
      
      if (!newToken || !newUser) {
        throw new Error('Invalid response from server');
      }
      
      // Store in localStorage first, then update state
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      // Update state after storing
      setToken(newToken);
      setUser(newUser);
      
      console.log('Login successful, token stored:', { 
        hasToken: !!newToken, 
        userId: newUser?.id,
        username: newUser?.username,
        role: newUser?.role
      });
    } catch (error: any) {
      console.error('Login error:', error);
      // Clear any existing token on login failure
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      throw new Error(error.response?.data?.error?.message || error.message || 'Login failed');
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token && !!user,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

